import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, LoginResponse, VerifyOtpResponse, AdminData } from '../../types';
import apiService from '../../services/api';

const loadStoredUser = (): AdminData | null => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw) as AdminData;
  } catch {
    return null;
  }
};

const initialState: AuthState = {
  isAuthenticated: !!localStorage.getItem('admin_token'),
  token: localStorage.getItem('admin_token'),
  user: loadStoredUser(),
  loading: false,
};

// Async thunks
export const staticLogin = createAsyncThunk(
  'auth/staticLogin',
  async (code: string) => {
    const response = await apiService.staticLogin(code);
    return response;
  }
);

export const verifyToken = createAsyncThunk(
  'auth/verifyToken',
  async (token: string) => {
    const response = await apiService.verifyToken(token);
    return response;
  }
);

export const getAdminData = createAsyncThunk(
  'auth/getAdminData',
  async () => {
    const response = await apiService.getAdminData();
    return response;
  }
);

export const otpLogin = createAsyncThunk(
  'auth/otpLogin',
  async (verifyData: { email: string; otp: string }) => {
    const response = await apiService.verifyOtp(verifyData);
    return response;
  }
);

export const fetchAdminProfile = createAsyncThunk(
  'auth/fetchAdminProfile',
  async (_, { rejectWithValue }) => {
    try {
      const admin = await apiService.getAdminProfile();
      return admin;
    } catch (error: any) {
      const status = error?.response?.status;
      return rejectWithValue({ status, message: error?.message });
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      localStorage.removeItem('admin_token');
      localStorage.removeItem('user');
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Static Login
      .addCase(staticLogin.pending, (state) => {
        state.loading = true;
      })
      .addCase(staticLogin.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.isAuthenticated = true;
          state.token = action.payload.token;
          state.user = action.payload.user;
          
          // Store token and user data in localStorage
          localStorage.setItem('admin_token', action.payload.token);
          localStorage.setItem('user', JSON.stringify(action.payload.user));
        }
      })
      .addCase(staticLogin.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
      })
      // Verify Token
      .addCase(verifyToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success && action.payload.data.valid) {
          state.isAuthenticated = true;
          state.user = action.payload.data.user;
          if (action.payload.data.user) {
            localStorage.setItem('user', JSON.stringify(action.payload.data.user));
          }
        } else {
          state.isAuthenticated = false;
          state.token = null;
          state.user = null;
          localStorage.removeItem('admin_token');
          localStorage.removeItem('user');
        }
      })
      .addCase(verifyToken.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        localStorage.removeItem('admin_token');
        localStorage.removeItem('user');
      })
      // Get Admin Data
      .addCase(getAdminData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAdminData.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.user = action.payload.data;
        }
      })
      .addCase(getAdminData.rejected, (state) => {
        state.loading = false;
      })
      // Refresh admin profile (role / identity from /admin/profile)
      .addCase(fetchAdminProfile.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload;
          state.isAuthenticated = true;
          localStorage.setItem('user', JSON.stringify(action.payload));
        }
      })
      .addCase(fetchAdminProfile.rejected, (state, action) => {
        const payload = action.payload as { status?: number } | undefined;
        if (payload?.status === 401 || payload?.status === 403) {
          state.isAuthenticated = false;
          state.token = null;
          state.user = null;
          localStorage.removeItem('admin_token');
          localStorage.removeItem('user');
        }
      })
      // OTP Login
      .addCase(otpLogin.pending, (state) => {
        state.loading = true;
      })
      .addCase(otpLogin.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload as any;
        
        // Handle response format: {"message": "Login successful", "token": "...", "admin": {...}}
        if (response.token && response.admin) {
          state.isAuthenticated = true;
          state.token = response.token;
          state.user = response.admin;
          
          // Store token and user data in localStorage
          localStorage.setItem('admin_token', response.token);
          localStorage.setItem('user', JSON.stringify(response.admin));
        }
      })
      .addCase(otpLogin.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
      });
  },
});

export const { logout, setLoading } = authSlice.actions;
export default authSlice.reducer; 